/**
 * WorkflowDesigner — the composable-workflow authoring UI (DEV-732 Part II).
 *
 * A drag-to-connect graph editor whose nodes are WorkflowSteps and edges are
 * WorkflowEdges (1:1 with the data model). Built on the same absolute-div + SVG
 * approach as the read-only WorkflowGraph (no extra graph dependency): drag a
 * node to move it, toggle Connect to draw a dependency edge between two nodes,
 * add modules from the palette, edit/delete the selected node, and Validate the
 * graph before publishing. Every mutation persists immediately via the authoring
 * CRUD endpoints.
 *
 * Lives in the Admin Console (superuser-gated by ConsoleLayout).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Link2, Loader2, Plus, Trash2, Wand2 } from 'lucide-react'
import { Button, Card, Input, Textarea } from '../../components/ui'
import { useUser } from '../../contexts/UserContext'
import { listVisibleTemplates } from '../../components/document-templates/api'
import { dedupeByKeyForScope } from '../../components/document-templates/cascade'
import type { Scope } from '../../components/document-templates/types'
import {
  workflowsApi,
  type AuthoringEdge,
  type AuthoringStepRead,
  type AuthoringWorkflow,
  type GraphValidation,
  type ModuleConfigField,
  type ModulePaletteItem,
} from '../../services/workflows'

const NODE_W = 220
const NODE_H = 92
const CANVAS_MIN_H = 520

export default function WorkflowDesigner({ basePath = '/console/workflows' }: { basePath?: string } = {}) {
  const { workflowId } = useParams<{ workflowId?: string }>()
  return workflowId
    ? <WorkflowCanvas workflowId={workflowId} basePath={basePath} />
    : <WorkflowList basePath={basePath} />
}

// ── list + create ─────────────────────────────────────────────────────────

function WorkflowList({ basePath = '/console/workflows' }: { basePath?: string }) {
  const navigate = useNavigate()
  const [workflows, setWorkflows] = useState<AuthoringWorkflow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [key, setKey] = useState('')
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    try {
      setWorkflows(await workflowsApi.listAuthoringWorkflows())
    } catch (e: any) {
      setError(e?.message || 'Failed to load workflows')
    } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  const create = async () => {
    if (!key.trim() || !name.trim()) return
    setCreating(true); setError(null)
    try {
      const wf = await workflowsApi.createWorkflow({ key: key.trim(), name: name.trim() })
      navigate(`${basePath}/${wf.id}`)
    } catch (e: any) {
      setError(e?.message || 'Could not create workflow')
    } finally { setCreating(false) }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-xl font-semibold text-foreground mb-1">Workflow Designer</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Author composable workflows: arrange module steps into a dependency graph.
      </p>

      <Card className="p-4 mb-6">
        <div className="text-sm font-medium text-foreground mb-3">New workflow</div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Key (slug)</span>
            <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="rapid_acquisition" />
          </label>
          <label className="flex flex-col gap-1 flex-1 min-w-[200px]">
            <span className="text-xs text-muted-foreground">Name</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Rapid Acquisition" />
          </label>
          <Button onClick={create} disabled={creating || !key.trim() || !name.trim()}>
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Create
          </Button>
        </div>
      </Card>

      {error && <div className="mb-3 text-sm text-destructive">{error}</div>}

      {loading ? (
        <div className="flex items-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>
      ) : (
        <ul className="space-y-2">
          {workflows.map((wf) => (
            <li key={wf.id}>
              <button
                onClick={() => navigate(`/console/workflows/${wf.id}`)}
                className="w-full text-left rounded-lg border border-border bg-card px-4 py-3 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-foreground">{wf.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {wf.steps.length} steps · v{wf.version} · {wf.is_published ? 'published' : 'draft'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">{wf.key}</div>
              </button>
            </li>
          ))}
          {workflows.length === 0 && (
            <li className="text-sm text-muted-foreground">No workflows yet — create one above.</li>
          )}
        </ul>
      )}
    </div>
  )
}

// ── canvas editor ───────────────────────────────────────────────────────────

export function WorkflowCanvas({ workflowId, basePath = '/console/workflows' }: { workflowId: string; basePath?: string }) {
  const navigate = useNavigate()
  const [wf, setWf] = useState<AuthoringWorkflow | null>(null)
  const [modules, setModules] = useState<ModulePaletteItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [selectedStepId, setSelectedStepId] = useState<number | null>(null)
  const [selectedEdgeId, setSelectedEdgeId] = useState<number | null>(null)
  const [connectFrom, setConnectFrom] = useState<number | null>(null)
  const [validation, setValidation] = useState<GraphValidation | null>(null)
  const canvasRef = useRef<HTMLDivElement | null>(null)
  const drag = useRef<{ id: number; dx: number; dy: number } | null>(null)

  const load = useCallback(async () => {
    try {
      const [w, m] = await Promise.all([
        workflowsApi.getAuthoringWorkflow(workflowId),
        workflowsApi.listModules(),
      ])
      setWf(w); setModules(m)
    } catch (e: any) {
      setError(e?.message || 'Failed to load workflow')
    }
  }, [workflowId])
  useEffect(() => { load() }, [load])

  const stepById = useMemo(() => {
    const map = new Map<number, AuthoringStepRead>()
    wf?.steps.forEach((s) => map.set(s.id, s))
    return map
  }, [wf])

  // ── drag to move ──────────────────────────────────────────────────────────
  const onNodePointerDown = (e: React.PointerEvent, step: AuthoringStepRead) => {
    e.preventDefault()
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return
    drag.current = {
      id: step.id,
      dx: e.clientX - rect.left - step.ui_x,
      dy: e.clientY - rect.top - step.ui_y,
    }
    setSelectedStepId(step.id)
    setSelectedEdgeId(null)
  }
  const onCanvasPointerMove = (e: React.PointerEvent) => {
    if (!drag.current || !wf) return
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return
    const nx = Math.max(0, e.clientX - rect.left - drag.current.dx)
    const ny = Math.max(0, e.clientY - rect.top - drag.current.dy)
    setWf({ ...wf, steps: wf.steps.map((s) => s.id === drag.current!.id ? { ...s, ui_x: nx, ui_y: ny } : s) })
  }
  const onCanvasPointerUp = async () => {
    const d = drag.current
    drag.current = null
    if (!d || !wf) return
    const step = wf.steps.find((s) => s.id === d.id)
    if (step) {
      try { await workflowsApi.updateStep(step.id, { ui_x: step.ui_x, ui_y: step.ui_y }) }
      catch (e: any) { setError(e?.message || 'Could not save position') }
    }
  }

  // ── node click (connect or select) ─────────────────────────────────────────
  const onNodeClick = async (step: AuthoringStepRead) => {
    if (connectFrom == null) { setSelectedStepId(step.id); setSelectedEdgeId(null); return }
    if (connectFrom === step.id) { setConnectFrom(null); return }
    try {
      await workflowsApi.createEdge({ workflow: wf!.id, from_step: connectFrom, to_step: step.id, kind: 'dependency' })
      setConnectFrom(null)
      await load()
    } catch (e: any) {
      setError(e?.message || 'Could not connect (invalid edge)')
      setConnectFrom(null)
    }
  }

  const addModule = async (m: ModulePaletteItem) => {
    if (!wf) return
    const n = wf.steps.length
    // Unique step_key within the workflow.
    const base = m.key
    let stepKey = base
    let i = 1
    const keys = new Set(wf.steps.map((s) => s.step_key))
    while (keys.has(stepKey)) { stepKey = `${base}_${i++}` }
    try {
      await workflowsApi.createStep({
        workflow: wf.id, module: m.key, step_key: stepKey, name: m.name,
        ui_x: 40 + (n % 4) * 260, ui_y: 40 + Math.floor(n / 4) * 150, step_index: n,
      })
      await load()
    } catch (e: any) {
      setError(e?.message || 'Could not add module')
    }
  }

  const deleteStep = async (step: AuthoringStepRead) => {
    const inUse = step.program_count > 0
      ? `\n\nThis step is used by ${step.program_count} program${step.program_count === 1 ? '' : 's'}. It will be removed from ${step.program_count === 1 ? 'it' : 'them'}, along with any progress on this step (documents and other outputs are kept).`
      : ''
    const ok = window.confirm(
      `Delete step "${step.name}"?\n\nSteps before it will be connected to the steps after it.${inUse}`,
    )
    if (!ok) return
    try { await workflowsApi.deleteStep(step.id) } catch (e: any) { setError(e?.message); return }
    setSelectedStepId(null)
    await load()
  }
  const deleteEdge = async (id: number) => {
    try { await workflowsApi.deleteEdge(id) } catch (e: any) { setError(e?.message); return }
    setSelectedEdgeId(null)
    await load()
  }
  const renameStep = async (id: number, name: string) => {
    setWf((w) => w ? { ...w, steps: w.steps.map((s) => s.id === id ? { ...s, name } : s) } : w)
    try { await workflowsApi.updateStep(id, { name }) } catch (e: any) { setError(e?.message) }
  }
  const describeStep = async (id: number, description: string) => {
    setWf((w) => w ? { ...w, steps: w.steps.map((s) => s.id === id ? { ...s, description } : s) } : w)
    try { await workflowsApi.updateStep(id, { description }) } catch (e: any) { setError(e?.message) }
  }
  const runValidate = async () => {
    try { setValidation(await workflowsApi.validateWorkflow(workflowId)) }
    catch (e: any) { setError(e?.message) }
  }

  if (error && !wf) return <div className="p-8 text-destructive">{error}</div>
  if (!wf) return <div className="p-8 flex items-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>

  const selected = selectedStepId != null ? stepById.get(selectedStepId) : null
  const width = Math.max(CANVAS_MIN_H, ...wf.steps.map((s) => s.ui_x + NODE_W)) + 60
  const height = Math.max(CANVAS_MIN_H, ...wf.steps.map((s) => s.ui_y + NODE_H)) + 60

  return (
    <div className="flex flex-col h-full">
      {/* toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border">
        <button onClick={() => navigate(basePath)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> All workflows
        </button>
        <span className="font-medium text-foreground ml-2">{wf.name}</span>
        <span className="text-xs text-muted-foreground">({wf.key})</span>
        <div className="flex-1" />
        <Button
          size="sm"
          variant={connectFrom != null ? 'default' : 'outline'}
          onClick={() => setConnectFrom(connectFrom != null ? null : (selectedStepId ?? null))}
          title="Toggle connect mode, then click a source node and a target node"
        >
          <Link2 className="w-4 h-4 mr-1.5" />
          {connectFrom != null ? 'Click target node…' : 'Connect'}
        </Button>
        <Button size="sm" variant="outline" onClick={runValidate}>
          <Wand2 className="w-4 h-4 mr-1.5" /> Validate
        </Button>
      </div>

      {error && <div className="px-4 py-2 text-sm text-destructive">{error}</div>}
      {validation && (
        <div className={`px-4 py-2 text-sm border-b border-border ${validation.valid ? 'text-green-600' : 'text-destructive'}`}>
          {validation.valid ? 'Graph is valid.' : `Invalid: ${validation.errors.join(' ')}`}
          {validation.warnings.length > 0 && (
            <div className="text-xs text-amber-600 mt-1">Warnings: {validation.warnings.join(' ')}</div>
          )}
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        {/* palette */}
        <aside className="w-56 border-r border-border bg-card overflow-auto p-2 shrink-0">
          <div className="text-xs font-semibold text-muted-foreground px-1 py-1">Modules</div>
          {modules.map((m) => (
            <button
              key={m.key}
              onClick={() => addModule(m)}
              className="w-full text-left rounded-md border border-border px-2 py-1.5 mb-1 hover:bg-muted transition-colors"
              title={m.description}
            >
              <div className="text-sm text-foreground flex items-center justify-between">
                <span className="truncate">{m.name}</span>
                <Plus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              </div>
              <div className="text-[11px] text-muted-foreground truncate">{m.key}</div>
            </button>
          ))}
        </aside>

        {/* canvas */}
        <div className="flex-1 overflow-auto bg-muted/20">
          <div
            ref={canvasRef}
            className="relative"
            style={{ width, height }}
            onPointerMove={onCanvasPointerMove}
            onPointerUp={onCanvasPointerUp}
            onClick={(e) => { if (e.target === e.currentTarget) { setSelectedStepId(null); setSelectedEdgeId(null) } }}
          >
            <svg className="absolute inset-0" width={width} height={height}>
              <defs>
                <marker id="wfd-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L8,3 L0,6 Z" className="fill-muted-foreground" />
                </marker>
              </defs>
              {wf.edges.map((e: AuthoringEdge) => {
                const from = stepById.get(e.from_step)
                const to = stepById.get(e.to_step)
                if (!from || !to) return null
                const x1 = from.ui_x + NODE_W, y1 = from.ui_y + NODE_H / 2
                const x2 = to.ui_x, y2 = to.ui_y + NODE_H / 2
                const sel = e.id === selectedEdgeId
                return (
                  <g key={e.id} onClick={() => { setSelectedEdgeId(e.id); setSelectedStepId(null) }} style={{ cursor: 'pointer' }}>
                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth={12} />
                    <line x1={x1} y1={y1} x2={x2} y2={y2}
                      className={sel ? 'stroke-primary' : 'stroke-muted-foreground/60'}
                      strokeWidth={sel ? 2.5 : 1.5} markerEnd="url(#wfd-arrow)" />
                  </g>
                )
              })}
            </svg>

            {wf.steps.map((s) => {
              const isSel = s.id === selectedStepId
              const isConnFrom = s.id === connectFrom
              return (
                <div
                  key={s.id}
                  onPointerDown={(e) => onNodePointerDown(e, s)}
                  onClick={(e) => { e.stopPropagation(); onNodeClick(s) }}
                  className={`absolute rounded-xl border-2 bg-card shadow-sm select-none cursor-grab active:cursor-grabbing
                    ${isConnFrom ? 'border-primary ring-2 ring-primary/40' : isSel ? 'border-primary' : 'border-border'}`}
                  style={{ left: s.ui_x, top: s.ui_y, width: NODE_W, minHeight: NODE_H }}
                >
                  <div className="p-3">
                    <div className="font-medium text-foreground text-sm truncate">{s.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{s.module}</div>
                    <div className="text-[11px] text-muted-foreground/70 mt-1">{s.step_key}</div>
                  </div>
                </div>
              )
            })}

            {wf.steps.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground pointer-events-none">
                Add modules from the palette to start building the graph.
              </div>
            )}
          </div>
        </div>

        {/* inspector */}
        <aside className="w-64 border-l border-border bg-card overflow-auto p-3 shrink-0">
          {selectedEdgeId != null ? (
            <div className="space-y-3">
              <div className="text-sm font-medium text-foreground">Edge</div>
              <Button size="sm" variant="outline" onClick={() => deleteEdge(selectedEdgeId)}>
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete edge
              </Button>
            </div>
          ) : selected ? (
            <div className="space-y-3">
              <div className="text-sm font-medium text-foreground">Step</div>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Name</span>
                <Input value={selected.name} onChange={(e) => renameStep(selected.id, e.target.value)} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Description</span>
                <Textarea
                  rows={4}
                  value={selected.description ?? ''}
                  onChange={(e) => describeStep(selected.id, e.target.value)}
                  placeholder="What this step does and why — shown to the user on the module page."
                  className="text-xs"
                />
              </label>
              <div className="text-xs text-muted-foreground">
                Module: <span className="text-foreground">{selected.module}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                Key: <span className="text-foreground">{selected.step_key}</span>
              </div>
              <ConfigEditor
                step={selected}
                module={modules.find((m) => m.key === selected.module) ?? null}
                onSaved={load}
                onError={setError}
              />
              <Button size="sm" variant="outline" onClick={() => deleteStep(selected)}>
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete step
              </Button>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              Select a node or edge to edit it. Drag nodes to arrange; use Connect to draw dependency edges.
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}

/**
 * Structured, per-module step settings — no raw JSON. Renders one control per
 * field in the module's `config_schema` (a select/toggle/input, or the dynamic
 * document-type picker), plus an optional agent-guidance textarea. Each change
 * persists immediately via the authoring CRUD endpoint.
 */
function ConfigEditor({ step, module, onSaved, onError }: {
  step: AuthoringStepRead
  module: ModulePaletteItem | null
  onSaved: () => void
  onError: (m: string) => void
}) {
  const fields = module?.config_schema ?? []
  const [config, setConfig] = useState<Record<string, unknown>>(step.config ?? {})
  const [instructions, setInstructions] = useState(step.agent_instructions ?? '')
  const [saving, setSaving] = useState(false)
  // Reset local state when a different step is selected.
  useEffect(() => {
    setConfig(step.config ?? {})
    setInstructions(step.agent_instructions ?? '')
  }, [step.id])

  const setField = async (key: string, value: unknown) => {
    const next = { ...config }
    if (value === '' || value === undefined || value === null) delete next[key]
    else next[key] = value
    setConfig(next)
    setSaving(true)
    try { await workflowsApi.updateStep(step.id, { config: next }); onSaved() }
    catch (e: any) { onError(e?.message || 'Could not save settings') }
    finally { setSaving(false) }
  }
  const saveInstructions = async () => {
    if (instructions === (step.agent_instructions ?? '')) return
    try { await workflowsApi.updateStep(step.id, { agent_instructions: instructions }) }
    catch (e: any) { onError(e?.message || 'Could not save guidance') }
  }

  return (
    <div className="space-y-3 border-t border-border pt-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-foreground">Settings</span>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
      </div>

      {fields.length === 0 ? (
        <p className="text-xs text-muted-foreground">This module has no settings to configure.</p>
      ) : (
        fields.map((field) => (
          <ConfigField
            key={field.key}
            field={field}
            value={config[field.key]}
            disabled={saving}
            onChange={(v) => setField(field.key, v)}
          />
        ))
      )}

      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">Agent guidance (optional)</span>
        <Textarea
          rows={3}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          onBlur={saveInstructions}
          placeholder="In-step helper guidance for the agent (not flow control)."
          className="text-xs"
        />
      </label>
    </div>
  )
}

/** Renders a single module config field as the right control for its type. */
function ConfigField({ field, value, disabled, onChange }: {
  field: ModuleConfigField
  value: unknown
  disabled: boolean
  onChange: (value: unknown) => void
}) {
  const str = value == null ? '' : String(value)
  const labelText = `${field.label}${field.required ? ' *' : ''}`

  if (field.type === 'document_template') {
    return <DocumentTypePicker value={str} disabled={disabled} onChange={onChange} label={labelText} />
  }
  if (field.type === 'select') {
    return (
      <label className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">{labelText}</span>
        <select
          value={str}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground disabled:opacity-50"
        >
          <option value="">Select…</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {field.help && <span className="text-[11px] text-muted-foreground/70">{field.help}</span>}
      </label>
    )
  }
  if (field.type === 'toggle') {
    return (
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="text-xs text-muted-foreground">{field.label}</span>
      </label>
    )
  }
  // 'text' — commit on blur so we don't PATCH on every keystroke.
  return <TextConfigField field={field} value={str} disabled={disabled} onChange={onChange} />
}

function TextConfigField({ field, value, disabled, onChange }: {
  field: ModuleConfigField; value: string; disabled: boolean; onChange: (value: string) => void
}) {
  const [local, setLocal] = useState(value)
  useEffect(() => { setLocal(value) }, [value])
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{field.label}{field.required ? ' *' : ''}</span>
      <Input
        value={local}
        disabled={disabled}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { if (local !== value) onChange(local) }}
        placeholder={field.help}
      />
      {field.help && <span className="text-[11px] text-muted-foreground/70">{field.help}</span>}
    </label>
  )
}

/**
 * Document-type picker for `document_generation` steps — lists the document
 * templates visible to the author (their office's customizations plus system
 * templates, deduped by family key), so a step names an existing document type
 * instead of a hand-typed `template_key`. Falls back silently to the JSON
 * editor if templates can't be loaded (e.g. no office scope).
 */
function DocumentTypePicker({ value, disabled, onChange, label = 'Document type' }: {
  value: string; disabled: boolean; onChange: (key: string) => void; label?: string
}) {
  const { user } = useUser()
  const [options, setOptions] = useState<{ key: string; name: string }[]>([])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const officeId = user?.office?.id ?? null
      const scope: Scope = officeId
        ? { level: 'office', ownerId: officeId }
        : { level: 'system' }
      try {
        const raw = await listVisibleTemplates({ scope_level: scope.level, office_id: officeId })
        const deduped = dedupeByKeyForScope(raw, scope)
        if (!cancelled) setOptions(deduped.map((t) => ({ key: t.key, name: t.name })))
      } catch {
        if (!cancelled) setOptions([])
      }
    })()
    return () => { cancelled = true }
  }, [user?.office?.id])

  // Surface an unknown/typed key so it isn't silently dropped from the select.
  const hasValue = value && options.some((o) => o.key === value)

  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground disabled:opacity-50"
      >
        <option value="">Select a document type…</option>
        {value && !hasValue && <option value={value}>{value} (custom)</option>}
        {options.map((o) => (
          <option key={o.key} value={o.key}>{o.name}</option>
        ))}
      </select>
    </label>
  )
}
